import { TaoProjectRefId } from "@hex/common";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "../store";

export type TaoTextFile = { content: string };
type FileReadState = {
  data?: TaoTextFile | null;
  status: "idle" | "loading" | "ready" | "error";
  requestId?: string;
  error?: string;
};
type TaoProjectFilesState = Partial<
  Record<TaoProjectRefId, Record<string, FileReadState | undefined>>
>;
type FilePath = { branchId: TaoProjectRefId; path: string };
type FileRequest = FilePath & { requestId: string };

const initialState: TaoProjectFilesState = {};
const EMPTY_FILE: FileReadState = { status: "idle" };

const slice = createSlice({
  name: "taoProjectFiles",
  initialState,
  reducers: {
    fileReadStarted(state, { payload }: PayloadAction<FileRequest>) {
      const files = (state[payload.branchId] ??= {});
      files[payload.path] = {
        data: files[payload.path]?.data,
        status: "loading",
        requestId: payload.requestId,
      };
    },
    fileReadSucceeded(
      state,
      { payload }: PayloadAction<FileRequest & { data: TaoTextFile | null }>,
    ) {
      const files = (state[payload.branchId] ??= {});
      if (files[payload.path]?.requestId === payload.requestId)
        files[payload.path] = { status: "ready", data: payload.data };
    },
    fileReadFailed(
      state,
      { payload }: PayloadAction<FileRequest & { error: string }>,
    ) {
      const file = state[payload.branchId]?.[payload.path];
      if (file?.requestId === payload.requestId) {
        file.status = "error";
        file.error = payload.error;
        file.requestId = undefined;
      }
    },
    setFile(
      state,
      { payload }: PayloadAction<FilePath & { data: TaoTextFile | null }>,
    ) {
      // Replacing the request id also prevents an older HTTP read from overwriting this edit.
      (state[payload.branchId] ??= {})[payload.path] = {
        status: "ready",
        data: payload.data,
      };
    },
    invalidateFiles(
      state,
      {
        payload,
      }: PayloadAction<{
        branchId: TaoProjectRefId;
        paths?: readonly string[];
      }>,
    ) {
      const files = state[payload.branchId];
      if (files == null) return;
      for (const path of payload.paths ?? Object.keys(files)) {
        const file = files[path];
        if (file != null) files[path] = { data: file.data, status: "idle" };
      }
    },
  },
});

export const taoProjectFilesReducer = slice.reducer;
export const taoProjectFilesActions = slice.actions;
export const selectTaoProjectFile = (
  state: RootState,
  branchId: TaoProjectRefId,
  path: string | null,
) => {
  const files = state.taoProjectFiles[branchId];
  return (
    (path != null && files != null && Object.hasOwn(files, path)
      ? files[path]
      : undefined) ?? EMPTY_FILE
  );
};
